import { useCallback, useEffect, useMemo } from 'react';
import { setPartialValidatorSectionMessages } from './validatorSlice';
import { validatorSectionTitles, ValidatorSection } from './validatorLib';
import { createDebugLog } from '@ydistri/utils';
import { ValidationArea } from '@ydistri/api-sdk';
import { useAppDispatch } from '../../../store';
import { TemplateOrCalculationWithValue } from '../../../apis/api';
import { skipToken } from '@reduxjs/toolkit/query';
import { reportQuery, useGetValidationProblemsQuery } from '../../../apis/apiValidationProblems';
import {
  reportMessages,
  reportStatus,
  sectionArea,
  ValidatorVerdict,
  verdictOf,
} from './problemsLib';
import { useApplicationData } from '../../../hooks/useApplicationData';

const debugLog = createDebugLog('Validator', 'useValidator');

/**
 * Feeds the validator state of one template or calculation from the six area reports of the server,
 * and returns the verdict read straight off the queries - not off the mirrored Redux messages, which
 * an effect installs one render later and which would let a failed report read as valid in between.
 */
export function useValidator(
  templateOrCalculation: TemplateOrCalculationWithValue,
): ValidatorVerdict {
  const dispatch = useAppDispatch();
  const { departments: hasDepartments } = useApplicationData();

  const templateId = templateOrCalculation.id;

  const reportArg = useCallback(
    (area: ValidationArea) =>
      templateOrCalculation.hasValidValue
        ? reportQuery({ type: templateOrCalculation.type, id: templateId }, area)
        : skipToken,
    [templateId, templateOrCalculation.hasValidValue, templateOrCalculation.type],
  );

  const stores = useGetValidationProblemsQuery(reportArg(ValidationArea.StoresAndDepartments));
  const categories = useGetValidationProblemsQuery(reportArg(ValidationArea.Categories));
  const regions = useGetValidationProblemsQuery(reportArg(ValidationArea.Regions));
  const skuClasses = useGetValidationProblemsQuery(reportArg(ValidationArea.SkuClasses));
  const productLists = useGetValidationProblemsQuery(reportArg(ValidationArea.ProductLists));
  const brands = useGetValidationProblemsQuery(reportArg(ValidationArea.Brands));

  const reports = useMemo(
    () => [
      { section: ValidatorSection.STORES_AND_DEPARTMENTS, query: stores },
      { section: ValidatorSection.CATEGORIES, query: categories },
      { section: ValidatorSection.REGIONS, query: regions },
      { section: ValidatorSection.SKU_CLASSES, query: skuClasses },
      { section: ValidatorSection.PRODUCT_LISTS, query: productLists },
      { section: ValidatorSection.BRANDS, query: brands },
    ],
    [brands, categories, productLists, regions, skuClasses, stores],
  );

  useEffect(() => {
    const titles = validatorSectionTitles(hasDepartments);
    reports.forEach(({ section, query }) => {
      const status = reportStatus(query);
      debugLog(section, sectionArea[section], status, query.data);
      dispatch(
        setPartialValidatorSectionMessages({
          templateId,
          section,
          messages: reportMessages(section, titles[section], {
            status,
            problems: query.data ?? [],
          }),
        }),
      );
    });
  }, [dispatch, hasDepartments, reports, templateId]);

  return useMemo(
    () =>
      verdictOf(
        reports.map(({ query }) => ({ status: reportStatus(query), problems: query.data ?? [] })),
      ),
    [reports],
  );
}
